import React, { useState, useContext, useRef } from 'react'
import { BrowserRouter, Route, Redirect, Switch } from 'react-router-dom'
import {
  ApolloProvider,
  ApolloClient,
  InMemoryCache,
  from,
} from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import cleanTypenameLink from './utils/cleanTypenameLink'
import { createUploadLink } from 'apollo-upload-client'
import { AuthContext, AuthRoutes } from '@dexter-experts/auth-react'
import Register from './pages/Register'
import Dashboard from './pages/Dashboard'
import Payment from './pages/Payment'
import Projects from './pages/Projects'
import Profile from './pages/Profile'
import Referral from './pages/Referral'
import Landing from './pages/Landing'
import PartnerLanding from './pages/PartnerLanding'
import PublicProjectLanding from './pages/PublicProject/PublicProjectLanding'
import PublicProjectAuthenticate from './pages/PublicProject/PublicProjectAuthenticate'
import PublicProjectRegister from './pages/PublicProject/PublicProjectRegister'
import PublicProjectUnqualified from './pages/PublicProject/PublicProjectUnqualified'
import Meeting from './pages/Meeting'
import ScrollToTop from './ScrollToTop'
import ExpertRoute from './components/ExpertRoute'
import PublicProjectRoute from './components/PublicProject/PublicProjectRoute'
import NavbarLayout from './components/NavbarLayout'
import Alert from './components/Alert'
import reportError from './utils/reportError'
import { DataProvider } from './contexts/DataContext'
import { UserProvider } from './contexts/UserContext'

const guestUserPages = (
  <Switch>
    <PublicProjectRoute
      path="/landing/projects/:id"
      component={PublicProjectLanding}
    />
    <PublicProjectRoute
      path="/landing/authenticate"
      component={PublicProjectAuthenticate}
    />
    <PublicProjectRoute
      path="/landing/invite-to-dexter"
      component={PublicProjectUnqualified}
    />
    <Route exact path="/partners/:partner" component={PartnerLanding} />
    <Route path="/" component={Landing} />
  </Switch>
)

const loggedInUserPages = (
  <UserProvider>
    <DataProvider>
      <Switch>
        <PublicProjectRoute
          path="/landing/projects/:id"
          component={PublicProjectLanding}
        />
        <PublicProjectRoute
          path="/landing/register"
          component={PublicProjectRegister}
        />
        <ExpertRoute path="/referral" component={Referral} />
        <ExpertRoute path="/profile/:page" component={Profile} />
        <ExpertRoute path="/profile" component={Profile} />
        <ExpertRoute path="/projects/:page" component={Projects} />
        <ExpertRoute path="/projects" component={Projects} />
        <ExpertRoute path="/meetings/:id" component={Meeting} />
        <ExpertRoute path="/payment" component={Payment} />
        <ExpertRoute path="/payment/:uri" component={Payment} />
        <ExpertRoute path="/dashboard" component={Dashboard} />
        <Route
          path="/register"
          render={() => (
            <NavbarLayout>
              <Register />
            </NavbarLayout>
          )}
        />
        <Route path="/" render={() => <Redirect to="/dashboard" />} />
      </Switch>
    </DataProvider>
  </UserProvider>
)

const ErrorMessage = ({ title, content, onClose }) => (
  <Alert title={title} content={content} onClose={onClose} />
)

const Routes = () => {
  const { token, authUser } = useContext(AuthContext)
  const tokenRef = useRef(token)
  const [error, setError] = useState(null)
  const [apolloClient] = useState(() => {
    const authLink = setContext(async (_, { headers }) => ({
      headers: {
        ...headers,
        ...(tokenRef.current && {
          authorization: `Bearer ${tokenRef.current}`,
        }),
      },
    }))

    const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
      const { skipGlobalErrorHandling, skipErrorDisplay, fatalError } =
        operation.getContext()

      if (skipGlobalErrorHandling) {
        return
      }

      const handledMessages = graphQLErrors
        ?.map((err) => err.message)
        .filter(Boolean)
      const isHandled = handledMessages?.length > 0

      // A handled error carries a message meant for the expert. Anything else
      // is a bug or an outage, and we want to hear about it.
      if (!isHandled) {
        reportError({ graphQLErrors, networkError, operation })
      }

      // Operations that surface their own failure must not also raise the
      // alert, or the expert gets told twice.
      if (skipErrorDisplay) {
        return
      }

      setError({
        fatal: !!fatalError,
        ...(isHandled
          ? { title: 'Error', content: handledMessages.join('\n') }
          : {
              title: 'Something went wrong',
              content:
                'Please try again or contact us at help@dexterexperts.com if the issue persists.',
            }),
      })
    })

    return new ApolloClient({
      link: from([
        errorLink,
        authLink,
        cleanTypenameLink,
        createUploadLink({
          uri: process.env.REACT_APP_GRAPHQL_BASE_URL,
        }),
      ]),
      cache: new InMemoryCache({
        typePolicies: {
          Project: {
            merge: true,
            fields: {
              calendar: { merge: false },
            },
          },
        },
      }),
    })
  })

  tokenRef.current = token

  const getPages = () => {
    if (authUser?.emailVerified) {
      return loggedInUserPages
    }
    return guestUserPages
  }

  return (
    <ApolloProvider client={apolloClient}>
      {/* Rendered alongside the app, not instead of it: Alert is a modal, and
          unmounting the tree would throw away anything half-filled in. */}
      {error && (
        <ErrorMessage
          title={error.title}
          content={error.content}
          onClose={() =>
            error.fatal ? window.location.reload() : setError(null)
          }
        />
      )}
      <BrowserRouter>
        <ScrollToTop />
        <Switch>
          <AuthRoutes />
          <Route render={() => getPages()} />
        </Switch>
      </BrowserRouter>
    </ApolloProvider>
  )
}

export default Routes
